import React, { useState } from 'react';
import {
  Container, Paper, TextField, Button, Typography,
  Box, Link, Alert, CircularProgress, InputAdornment, IconButton,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person, AdminPanelSettings } from '@mui/icons-material';
import { Link as RouterLink, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { normalizeUserFacingErrorMessage } from '@utils/errorMessage';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const location = useLocation();

  const handleSubmit = async (event?: React.FormEvent | React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Pentru toti utilizatorii, redirectionarea se face automat prin isAuthenticated
        // Master admin-ii vor fi redirectionati catre /master-admin
        // Utilizatorii normali vor fi redirectionati catre /dashboard
      } else {
        setError(
          normalizeUserFacingErrorMessage(result.message ?? 'Credentiale incorecte')
        );
      }
    } catch (err: unknown) {
      setError(normalizeUserFacingErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (isAuthenticated) {
    const from = (location.state as any)?.from?.pathname;
    
    // Redirectioneaza pe baza rolului
    if (user?.role === 'master_admin') {
      return <Navigate to="/master-admin" replace />;
    } else if (user?.role === 'clinic_admin') {
      // Daca utilizatorul era pe o ruta /clinic/*, redirectioneaza inapoi la acea ruta
      if (from && from.startsWith('/clinic/')) {
        return <Navigate to={from} replace />;
      }
      // Altfel, redirectioneaza INTOTDEAUNA la dashboard-ul de clinic admin
      return <Navigate to="/clinic" replace />;
    } else if (user && (user.role === 'veterinarian' || user.role === 'doctor')) {
      // Pentru veterinari si doctori, redirectioneaza catre dashboard-ul specific
      return <Navigate to={from || '/doctor'} replace />;
    } else if (user && (user.role === 'shelter_admin' || user.role === 'shelter_staff')) {
      // Pentru utilizatorii de adapost, redirectioneaza catre dashboard-ul de adapost
      return <Navigate to={from || '/shelter'} replace />;
    } else {
      // Pentru utilizatorii normali (client), redirectioneaza catre dashboard
      return <Navigate to={from || '/dashboard'} replace />;
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <form onSubmit={handleSubmit}>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  mb: 2
                }}
              >
                <Person sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                Health V2
              </Typography>
              <Typography variant="h5" sx={{ mb: 1 }}>
                Autentificare Utilizator
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pentru veterinari, clinica si clienti
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              required fullWidth label="Email" type="email" margin="normal"
              value={email} onChange={e => setEmail(e.target.value)}
              inputProps={{ autoComplete: 'email' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Email /></InputAdornment>
              }}
            />

            <TextField
              required fullWidth label="Parola"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={password} onChange={e => setPassword(e.target.value)}
              inputProps={{ autoComplete: 'current-password' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePasswordVisibility}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Autentificare'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/forgot-password">Ai uitat parola?</Link>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link component={RouterLink} to="/account-type">Nu ai cont? Inregistreaza-te</Link>
            </Box>
            
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                sau
              </Typography>
            </Divider>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Administrator master?{' '}
                <Link component={RouterLink} to="/login/admin" variant="body2">
                  Autentificare admin
                </Link>
              </Typography>
            </Box>
          </Paper>
        </form>
      </Box>
    </Container>
  );
};

export default Login; 
